import ExcelJS from 'exceljs'
import { Column, ColumnGroup, GridApi, IRowNode, isColumnGroup } from 'ag-grid-enterprise'
import sanitizeFilename from '../../../utils/sanitizeFilename'
import { colorToArgb } from '../../../utils/color.utils'
import { autoDownload } from '../../../utils/helpers'
import { CellExportMetadataProvider, HeaderExportMetadataProvider } from './excelExportTypes'

export interface ExcelExportSheetInput {
  sheetName: string
  api: GridApi
  getCellMetadata: CellExportMetadataProvider
  getHeaderMetadata?: HeaderExportMetadataProvider
  /**
   * When given, resolves columns via `api.getColumn(id)` in this exact order
   * instead of `api.getAllDisplayedColumns()`. Lets a grid include hidden
   * export-only columns (e.g. `hide: true` colDefs) and control ordering.
   */
  columnKeys?: string[]
  /** Data rows for which this returns true are omitted from the sheet entirely. */
  shouldSkipRow?: (node: IRowNode) => boolean
}

/* ExcelJS's API requires mutating cell/row/column properties directly, so
 * `no-param-reassign` is disabled for the write helpers below. */
/* eslint-disable no-param-reassign */
const applyCellStyle = (
  cell: ExcelJS.Cell,
  meta: {
    value: unknown
    numberFormat?: string
    bold?: boolean
    italic?: boolean
    bgColor?: string
    align?: 'left' | 'right' | 'center'
  }
) => {
  // Numeric values keep their type so Excel can calculate with them
  if (typeof meta.value === 'number' && !Number.isNaN(meta.value)) {
    cell.value = meta.value
    if (meta.numberFormat) {
      cell.numFmt = meta.numberFormat
    }
  } else if (meta.value == null) {
    cell.value = null
  } else {
    cell.value = String(meta.value)
  }

  if (meta.bold || meta.italic) {
    cell.font = { bold: !!meta.bold, italic: !!meta.italic }
  }

  const argb = colorToArgb(meta.bgColor)
  if (argb) {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb }
    }
  }

  if (meta.align) {
    cell.alignment = { horizontal: meta.align }
  }
}
/* eslint-enable no-param-reassign */

interface GroupHeaderCell {
  label: string
  startCol: number
  endCol: number
}

const getMaxGroupDepth = (items: (Column | ColumnGroup)[]): number => {
  let max = 0
  for (const item of items) {
    if (isColumnGroup(item)) {
      const children = item.getDisplayedChildren()
      const childDepth = children?.length ? getMaxGroupDepth(children) : 0
      max = Math.max(max, 1 + childDepth)
    }
  }
  return max
}

const collectLevelCells = (
  items: (Column | ColumnGroup)[],
  targetLevel: number,
  currentLevel: number,
  startCol: number
): { cells: GroupHeaderCell[]; nextCol: number } => {
  const cells: GroupHeaderCell[] = []
  let col = startCol

  for (const item of items) {
    if (isColumnGroup(item)) {
      const leafCount = item.getDisplayedLeafColumns().length
      if (currentLevel === targetLevel) {
        const def = item.getColGroupDef()
        const label = (!item.isPadding() && def?.headerName) || ''
        cells.push({ label, startCol: col, endCol: col + leafCount - 1 })
      } else {
        const children = item.getDisplayedChildren()
        if (children?.length) {
          const result = collectLevelCells(children, targetLevel, currentLevel + 1, col)
          cells.push(...result.cells)
        }
      }
      col += leafCount
    } else {
      if (currentLevel === targetLevel) {
        cells.push({ label: '', startCol: col, endCol: col })
      }
      col += 1
    }
  }

  return { cells, nextCol: col }
}

const buildGroupHeaderRows = (api: GridApi): GroupHeaderCell[][] => {
  const topItems = api.getAllDisplayedColumnGroups()
  if (!topItems?.length) return []

  const maxDepth = getMaxGroupDepth(topItems)
  if (maxDepth === 0) return []

  const levels: GroupHeaderCell[][] = []
  for (let level = 0; level < maxDepth; level += 1) {
    const { cells } = collectLevelCells(topItems, level, 0, 1)
    levels.push(cells)
  }
  return levels
}

/**
 * Builds an ExcelJS workbook from one or more ag-Grid instances.
 * Each sheet is driven by a `CellExportMetadataProvider` supplied by the
 * caller — this is where per-grid styling logic (colors, number format,
 * fonts) is resolved.
 */
export const buildCustomReportWorkbook = async (sheets: ExcelExportSheetInput[]): Promise<ExcelJS.Workbook> => {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Finadeck'
  workbook.created = new Date()

  const usedLowerNames = new Set<string>()

  for (const sheet of sheets) {
    // Excel sheet name: max 31 chars, forbidden chars: * ? : \ / [ ]
    const baseName = sheet.sheetName.replace(/[*?:\\/[\]]/g, ' ').slice(0, 31) || 'Sheet'
    let candidate = baseName
    let counter = 1
    while (usedLowerNames.has(candidate.toLowerCase())) {
      const suffix = ` (${counter})`
      candidate = baseName.slice(0, 31 - suffix.length) + suffix
      counter += 1
    }
    usedLowerNames.add(candidate.toLowerCase())
    const ws = workbook.addWorksheet(candidate)
    ws.properties.outlineProperties = { summaryBelow: false, summaryRight: false }
    let maxOutlineLevel = 0
    const columns = sheet.columnKeys
      ? (sheet.columnKeys.map(id => sheet.api.getColumn(id)).filter(Boolean) as Column[])
      : sheet.api.getAllDisplayedColumns()

    // --- Column group header rows ---
    const groupLevels = sheet.columnKeys ? [] : buildGroupHeaderRows(sheet.api)
    /* eslint-disable no-param-reassign */
    for (const level of groupLevels) {
      const rowValues = Array(columns.length).fill(null)
      for (const cell of level) {
        rowValues[cell.startCol - 1] = cell.label
      }
      const groupRow = ws.addRow(rowValues)
      groupRow.eachCell({ includeEmpty: false }, cell => {
        cell.font = { bold: true }
        cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' }
      })
      for (const cell of level) {
        if (cell.startCol < cell.endCol && cell.label) {
          ws.mergeCells(groupRow.number, cell.startCol, groupRow.number, cell.endCol)
        }
      }
    }
    /* eslint-enable no-param-reassign */

    // --- Leaf header row ---
    const headerCells = columns.map(column => {
      if (sheet.getHeaderMetadata) {
        return sheet.getHeaderMetadata(column)
      }
      const colDef = column.getColDef()
      return {
        name: colDef.headerName ?? column.getColId(),
        bold: true
      }
    })

    const headerRow = ws.addRow(headerCells.map(h => h.name))
    /* eslint-disable no-param-reassign */
    headerRow.eachCell((cell, colNumber) => {
      const meta = headerCells[colNumber - 1]
      cell.font = { bold: meta.bold !== false }
      const argb = colorToArgb(meta.bgColor)
      if (argb) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } }
      }
      cell.alignment = {
        wrapText: true,
        vertical: 'top',
        horizontal: colNumber > 1 ? 'right' : 'left'
      }
    })
    /* eslint-enable no-param-reassign */

    // --- Data rows ---
    sheet.api.forEachNodeAfterFilterAndSort(node => {
      if (!node.data) return
      if (sheet.shouldSkipRow?.(node)) return
      const row = ws.addRow(Array(columns.length).fill(null))
      columns.forEach((column, index) => {
        const meta = sheet.getCellMetadata(node, column, sheet.api)
        const cell = row.getCell(index + 1)
        applyCellStyle(cell, meta)
      })
      if (node.level > 0) {
        row.outlineLevel = node.level
        if (node.level > maxOutlineLevel) maxOutlineLevel = node.level
      }
    })
    if (maxOutlineLevel > 0) {
      ws.properties.outlineLevelRow = maxOutlineLevel
    }

    /* eslint-disable no-param-reassign */
    ws.columns.forEach((col, index) => {
      col.width = index === 0 ? 40 : 20
    })
    /* eslint-enable no-param-reassign */
  }

  return workbook
}

/**
 * Builds the workbook and triggers a browser download.
 */
export const exportCustomReportToExcel = async (sheets: ExcelExportSheetInput[], fileName: string): Promise<void> => {
  if (sheets.length === 0) return

  const workbook = await buildCustomReportWorkbook(sheets)
  const buffer = await workbook.xlsx.writeBuffer()

  const sanitized = sanitizeFilename(fileName, 'report', 'xlsx')
  autoDownload(sanitized, buffer)
}
